import { JSONContent } from "@tiptap/react";
import { BaseInterface, Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { ConfigStep } from "../../utils/configuration";
import execute from "../http/execute";
import failure from "../http/failure";
import { get, patch, post } from "../http/request";
import success from "../http/success";
import { FEInstitution } from "./configuration";

export interface InstitutionWebhook {
    endpoint: string;
    signature: string;
}

export interface InstitutionAPI {
    enabled: boolean;
    tenantId?: string;
}

export interface InstitutionClient {
    id: string;
    name: string;
    whiteLabelEmail: {
        enabled: boolean;
        content: {
            json: JSONContent;
            html?: string;
        };
    };
}

type EmailType = 'magicLink';

export interface InstitutionEmail {
    type: EmailType;
    enabled: boolean;
    emailWhiteLabel?: {
        enabled: boolean;
        subject: string;
        content: {
            json: JSONContent;
            html?: string;
        };
    };
}

export interface IInstitution extends BaseInterface, FEInstitution {
    host: string;
    cidRequired: boolean;
    requireLast4Tin: boolean;
    anonymousCreate: boolean;
    webhooks: InstitutionWebhook[];
    api?: InstitutionAPI;
    emails: InstitutionEmail[];
}

export const GET_INSTITUTIONS_REQUEST = 'institution/GET_INSTITUTIONS_REQUEST';
export const GET_INSTITUTIONS_SUCCESS = 'institution/GET_INSTITUTIONS_SUCCESS';
export const GET_INSTITUTIONS_FAILURE = 'institution/GET_INSTITUTIONS_FAILURE';

export const GET_INSTITUTION_CLIENTS_REQUEST = 'institution/GET_INSTITUTION_CLIENTS_REQUEST';
export const GET_INSTITUTION_CLIENTS_SUCCESS = 'institution/GET_INSTITUTION_CLIENTS_SUCCESS';
export const GET_INSTITUTION_CLIENTS_FAILURE = 'institution/GET_INSTITUTION_CLIENTS_FAILURE';

export const GET_DEFAULT_TRUEMARK_REQUEST = 'institution/GET_DEFAULT_TRUEMARK_REQUEST';
export const GET_DEFAULT_TRUEMARK_SUCCESS = 'institution/GET_DEFAULT_TRUEMARK_SUCCESS';
export const GET_DEFAULT_TRUEMARK_FAILURE = 'institution/GET_DEFAULT_TRUEMARK_FAILURE';

export const POST_INSTITUTION_CLIENTS_REQUEST = 'institution/POST_INSTITUTION_CLIENTS_REQUEST';
export const POST_INSTITUTION_CLIENTS_SUCCESS = 'institution/POST_INSTITUTION_CLIENTS_SUCCESS';
export const POST_INSTITUTION_CLIENTS_FAILURE = 'institution/POST_INSTITUTION_CLIENTS_FAILURE';

export const CREATE_INSTITUTION_REQUEST = 'institution/CREATE_INSTITUTION_REQUEST';
export const CREATE_INSTITUTION_SUCCESS = 'institution/CREATE_INSTITUTION_SUCCESS';
export const CREATE_INSTITUTION_FAILURE = 'institution/CREATE_INSTITUTION_FAILURE';

export const PATCH_INSTITUTION_REQUEST = 'institution/PATCH_INSTITUTION_REQUEST';
export const PATCH_INSTITUTION_SUCCESS = 'institution/PATCH_INSTITUTION_SUCCESS';
export const PATCH_INSTITUTION_FAILURE = 'institution/PATCH_INSTITUTION_FAILURE';

export const PATCH_INSTITUTION_STEPS_REQUEST = 'institution/PATCH_INSTITUTION_STEPS_REQUEST';
export const PATCH_INSTITUTION_STEPS_SUCCESS = 'institution/PATCH_INSTITUTION_STEPS_SUCCESS';
export const PATCH_INSTITUTION_STEPS_FAILURE = 'institution/PATCH_INSTITUTION_STEPS_FAILURE';

export const PATCH_INSTITUTION_STEP_REQUEST = 'institution/PATCH_INSTITUTION_STEP_REQUEST';
export const PATCH_INSTITUTION_STEP_SUCCESS = 'institution/PATCH_INSTITUTION_STEP_SUCCESS';
export const PATCH_INSTITUTION_STEP_FAILURE = 'institution/PATCH_INSTITUTION_STEP_FAILURE';

export const ADD_EDITOR_IMAGE_REQUEST = 'institution/ADD_EDITOR_IMAGE_REQUEST';
export const ADD_EDITOR_IMAGE_SUCCESS = 'institution/ADD_EDITOR_IMAGE_SUCCESS';
export const ADD_EDITOR_IMAGE_FAILURE = 'institution/ADD_EDITOR_IMAGE_FAILURE';

export const SET_TEST_INSTITUTION = 'institution/SET_TEST_INSTITUTION';

const initialState: {
    institutions: IInstitution[];
    testInstitution?: IInstitution;
    clients: { [institutionId: string]: InstitutionClient[]; };
} = {
    institutions: [],
    clients: {},
}

type ActionParams =
    | { type: typeof GET_INSTITUTIONS_SUCCESS; data: IInstitution[]; }
    | { type: typeof SET_TEST_INSTITUTION; id?: string; }
    | { type: typeof PATCH_INSTITUTION_SUCCESS; data: IInstitution; }
    | { type: typeof PATCH_INSTITUTION_STEPS_SUCCESS; data: IInstitution; }
    | { type: typeof CREATE_INSTITUTION_SUCCESS; data: IInstitution; }
    | { type: typeof GET_INSTITUTION_CLIENTS_SUCCESS; data: { clients: InstitutionClient[]; institutionId: string; }; }
    | { type: typeof POST_INSTITUTION_CLIENTS_SUCCESS; data: { clients: InstitutionClient[]; institutionId: string; }; };

function reducer(state = initialState, action: ActionParams): typeof initialState {
    switch (action.type) {
        case GET_INSTITUTIONS_SUCCESS:
            return {
                ...state,
                institutions: action.data,
            };

        case CREATE_INSTITUTION_SUCCESS:
            return {
                ...state,
                institutions: [...state.institutions, action.data]
            }

        case SET_TEST_INSTITUTION:
            const testInstitution = state.institutions.find(({ id }) => id === action.id);

            return {
                ...state,
                testInstitution,
            };

        case PATCH_INSTITUTION_SUCCESS:
        case PATCH_INSTITUTION_STEPS_SUCCESS:
            return {
                ...state,
                institutions: state.institutions.map((inst) => (inst.id === action.data.id ? action.data : inst)),
                testInstitution: action.data,
            }

        case GET_INSTITUTION_CLIENTS_SUCCESS:
        case POST_INSTITUTION_CLIENTS_SUCCESS:
            return {
                ...state,
                clients: {
                    ...state.clients,
                    [action.data.institutionId]: action.data.clients,
                },
            };

        default: return state;
    }
}

export const getInstitutions = (payload: Payload) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_INSTITUTIONS_REQUEST,
        payload,
    });

    execute(get, '/institution', payload.input, payload.headers)
        .then(success(dispatch, GET_INSTITUTIONS_SUCCESS, payload))
        .catch(failure(dispatch, GET_INSTITUTIONS_FAILURE, payload));
};

export const getInstitutionClients = (payload: Payload<{ institutionId: string; }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_INSTITUTION_CLIENTS_REQUEST,
        payload,
    });

    execute(get, '/institution', { type: 'clients', ...payload.input }, payload.headers)
        .then(success(dispatch, GET_INSTITUTION_CLIENTS_SUCCESS, payload))
        .catch(failure(dispatch, GET_INSTITUTION_CLIENTS_FAILURE, payload));
};

export const updateInstitutionClients = (payload: Payload<{ institutionId: string; id: string; clients: InstitutionClient[] }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: POST_INSTITUTION_CLIENTS_REQUEST,
        payload,
    });

    execute(patch, '/institution', { type: 'clients', ...payload.input }, payload.headers)
        .then(success(dispatch, POST_INSTITUTION_CLIENTS_SUCCESS, payload))
        .catch(failure(dispatch, POST_INSTITUTION_CLIENTS_FAILURE, payload));
};

export const createInstitution = (payload: Payload<Partial<IInstitution>>) => (dispatch: AppDispatch) => {
    dispatch({
        type: CREATE_INSTITUTION_REQUEST,
        payload,
    });

    execute(post, '/institution', payload.input, payload.headers)
        .then(success(dispatch, CREATE_INSTITUTION_SUCCESS, payload))
        .catch(failure(dispatch, CREATE_INSTITUTION_FAILURE, payload));
};

export const updateInstitution = (payload: Payload<Partial<IInstitution>>) => (dispatch: AppDispatch) => {
    dispatch({
        type: PATCH_INSTITUTION_REQUEST,
        payload,
    });

    execute(patch, '/institution', payload.input, payload.headers)
        .then(success(dispatch, PATCH_INSTITUTION_SUCCESS, payload))
        .catch(failure(dispatch, PATCH_INSTITUTION_FAILURE, payload));
};

export const updateInstitutionSteps = (payload: Payload<{ id: string; institutionId: string; services: ConfigStep[] }>, type?: 'params') => (dispatch: AppDispatch) => {
    dispatch({
        type: PATCH_INSTITUTION_STEPS_REQUEST,
        payload,
    });

    execute(patch, '/institution', { ...payload.input, type }, payload.headers)
        .then(success(dispatch, PATCH_INSTITUTION_STEPS_SUCCESS, payload))
        .catch(failure(dispatch, PATCH_INSTITUTION_STEPS_FAILURE, payload))
}

export const addEditorImage = (payload: Payload<{ id: string; institutionId: string;[key: string]: any }>, type?: 'params') => async (dispatch: AppDispatch): Promise<{ url: string; } | null> => {
    dispatch({
        type: ADD_EDITOR_IMAGE_REQUEST,
        payload,
    });

    try {
        const result = await execute(post, '/editor/image', { ...payload.input, type }, payload.headers)
        success(dispatch, ADD_EDITOR_IMAGE_SUCCESS, payload)(result)
        return result.data;
    } catch (err) {
        failure(dispatch, ADD_EDITOR_IMAGE_FAILURE, payload)(err)
        return null;
    }
}

export const getDefaultTrueMarkDocument = (payload: Payload<void>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_DEFAULT_TRUEMARK_REQUEST,
        payload,
    });

    execute(get, '/institution', {type: 'tm-document'}, payload.headers)
        .then(success(dispatch, GET_DEFAULT_TRUEMARK_SUCCESS, payload))
        .catch(failure(dispatch, GET_DEFAULT_TRUEMARK_FAILURE, payload))
}

export default reducer;
